import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getBaseUrl } from "../config";
import { showSnackbar } from "./snackbar";

const baseUrl = getBaseUrl();

export const createKnowledgeBotSessionId = () => crypto.randomUUID();

const parseKbSseBuffer = (buffer) => {
  const parts = buffer.split("\n\n");
  const remainder = parts.pop();
  const chunks = [];
  let finalEvent = null;

  for (const raw of parts) {
    const lines = raw.split("\n").map((l) => l.replace(/\r$/, ""));
    const dataLines = lines
      .filter((l) => l.startsWith("data:"))
      .map((l) => l.slice(5).trimStart());

    if (!dataLines.length) continue;

    const joined = dataLines.join("\n");

    if (joined.startsWith("{") && joined.endsWith("}")) {
      try {
        const obj = JSON.parse(joined);

        if (obj?.event === "final") {
          finalEvent = obj;
          continue;
        }

        if (obj?.event === "token" && obj.content) {
          chunks.push(obj.content);
          continue;
        }
      } catch (e) {
        // fall through to raw text handling
      }
    }

    for (const d of dataLines) chunks.push(d);
  }

  return { remainder, chunks, finalEvent };
};

const streamKnowledgeBotChat = async (body, { dispatch }) => {
  const response = await fetch(`${baseUrl}/knowledge-bot/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });

  if (!response.ok) {
    dispatch(
      showSnackbar({
        message: "Knowledge Bot request failed",
        severity: "error",
      }),
    );
    throw new Error(response.statusText || "Knowledge Bot request failed");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    const { remainder, chunks, finalEvent } = parseKbSseBuffer(buffer);
    buffer = remainder;

    if (chunks.length) {
      for (const c of chunks) dispatch(addKbData(c));
    }

    if (finalEvent) {
      dispatch(setKbFinalEvent(finalEvent));
    }
  }

  if (buffer.trim()) {
    const { chunks, finalEvent } = parseKbSseBuffer(`${buffer}\n\n`);
    if (chunks.length) {
      for (const c of chunks) dispatch(addKbData(c));
    }
    if (finalEvent) {
      dispatch(setKbFinalEvent(finalEvent));
    }
  }
};

export const kbFetchStreamData = createAsyncThunk(
  "knowledgeBot/fetchStreamData",
  async (payload = {}, { dispatch }) => {
    const sessionId = payload.sessionId || createKnowledgeBotSessionId();

    await streamKnowledgeBotChat(
      {
        session_id: sessionId,
        message: payload.message,
        username: payload.username || "anonymous",
      },
      { dispatch },
    );

    return { sessionId };
  },
);

export const kbPostChatStream = createAsyncThunk(
  "knowledgeBot/postChatStream",
  async (payload = {}, { dispatch }) => {
    await streamKnowledgeBotChat(payload.data, { dispatch });
    return { sessionId: payload.data?.session_id || "" };
  },
);

const knowledgeBotStreamSlice = createSlice({
  name: "knowledgeBotStream",
  initialState: {
    data: [],
    loading: false,
    streamingEnded: false,
    sessionId: "",
    finalEvent: null,
  },
  reducers: {
    addKbData(state, action) {
      state.data.push(action.payload);
    },
    setKbFinalEvent(state, action) {
      state.finalEvent = action.payload;
    },
    clearKbStreamData(state, action) {
      state.streamingEnded = action.payload;
      state.data = [];
      state.finalEvent = null;
    },
    setKbSessionId(state, action) {
      state.sessionId = action.payload || "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(kbFetchStreamData.pending, (state) => {
        state.loading = true;
        state.data = [];
        state.finalEvent = null;
        state.streamingEnded = false;
      })
      .addCase(kbFetchStreamData.fulfilled, (state, action) => {
        state.loading = false;
        state.streamingEnded = true;
        state.sessionId = action.payload?.sessionId || state.sessionId;
      })
      .addCase(kbFetchStreamData.rejected, (state) => {
        state.loading = false;
        state.streamingEnded = true;
      })
      .addCase(kbPostChatStream.pending, (state) => {
        state.loading = true;
        state.data = [];
        state.finalEvent = null;
        state.streamingEnded = false;
      })
      .addCase(kbPostChatStream.fulfilled, (state, action) => {
        state.loading = false;
        state.streamingEnded = true;
        if (action.payload?.sessionId) {
          state.sessionId = action.payload.sessionId;
        }
      })
      .addCase(kbPostChatStream.rejected, (state) => {
        state.loading = false;
        state.streamingEnded = true;
      });
  },
});

export const {
  addKbData,
  setKbFinalEvent,
  clearKbStreamData,
  setKbSessionId,
} = knowledgeBotStreamSlice.actions;

export default knowledgeBotStreamSlice.reducer;
